<template>
  <article
    class="module is-relative is-flex is-align-items-center is-flex-direction-column"
    style="margin-bottom: 150px !important;"
    v-if="formData"
  >

    <div class="is-flex is-flex-grow-0 is-flex-direction-row is-justify-content-center is-align-items-center mt-6" style="z-index: 10; max-width: 500px;">
      <google-logo class="svg-icon" v-if="isGoogleSummit2024"></google-logo>
    </div>

    <div
      class="is-radius-xlarge is-flex-grow-1 google-form-container"
      ref="formContainer"
    >
      <div class="box p-4 google-form-container__content">
        <h2
        class="title primary-text-color is-3 has-text-centered mb-4 is-flex is-flex-direction-row is-justify-content-center is-align-items-center"
        v-if="title"
      >
          <p class="is-flex-grow-1" v-if="isSuccess || passwordSet">
            {{ $t("custom.google_confirmation_title") }}
          </p>
          <p class="is-flex-grow-1" v-else>
            {{ title }}
          </p>
        </h2>

        <template v-if="currentUserTicket">
          <div class="columns is-centered">
            <div class="column">
              <div class="has-text-centered">
                <p class="subtitle is-4">
                  {{ $t("custom.google_already_have_ticket") }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="!tokenQuery && !currentUser">
          <div class="columns is-centered">
            <div class="column">
              <div class="has-text-centered">
                <p class="subtitle is-4">
                  {{ $t("custom.google_confirmation_invalid_link") }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="isSecondStep">
          <edit-password-form
            class="mt-2"
            ref="form"
            :error="error"
            @submit="updatePassword"
          ></edit-password-form>
        </template>

        <template v-else-if="isSuccess">
          <div class="columns is-centered">
            <div class="column">
              <div class="has-text-centered">
                <p class="subtitle is-4">
                  <template v-if="isAlreadyFilled && !passwordSet">
                    {{ $t("custom.google_confirmation_subtitle_already_filled") }}
                  </template>
                  <template v-else-if="!passwordSet && tokenQuery">
                    {{ $t("custom.google_confirmation_subtitle_logged_in") }}
                  </template>
                  <template v-else-if="!passwordSet && !tokenQuery">
                    {{ $t("custom.google_confirmation_subtitle") }}
                  </template>
                  <template v-else-if="passwordSet && currentUser">
                    {{ $t("custom.google_description_after_password_is_set") }}
                  </template>
                  <template v-else-if="passwordSet && tokenQuery">
                    {{ $t("custom.google_description_after_password_is_set_2") }}
                  </template>
                </p>
                <template v-if="!passwordSet && tokenQuery">
                  <button class="button is-primary mt-2" @click="openChangePassword">
                    {{ $t("auth.set_password_button") }}
                  </button>
                </template>
                <template v-if="passwordSet && currentUser">
                  <button class="button is-primary mt-2" @click="openMyProfile">
                    {{ $t("custom.google_description_after_password_is_set_button") }}
                  </button>
                  <p class="subtitle is-4 mt-3">
                    {{ $t("custom.google_description_after_password_is_set_2") }}
                  </p>
                </template>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="isFirstStep">
          <div class="field">
            <label class="label has-text-primary">{{
              $t("auth.auth_firstname_placeholder")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                :placeholder="$t('auth.auth_firstname_placeholder')"
                :class="{ 'is-danger': $v.firstName.$error && validateForm }"
                v-model.trim="$v.firstName.$model"
              />
            </div>
            <template v-if="$v.firstName.$error && validateForm">
              <p v-if="!$v.firstName.required" class="help is-danger">
                {{ $t("auth.error_first_name") }}
              </p>
            </template>
          </div>

          <div class="field">
            <label class="label has-text-primary">{{
              $t("auth.auth_lastname_placeholder")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                :placeholder="$t('auth.auth_lastname_placeholder')"
                :class="{ 'is-danger': $v.lastName.$error && validateForm }"
                v-model.trim="$v.lastName.$model"
              />
            </div>
            <template v-if="$v.lastName.$error && validateForm">
              <p v-if="!$v.lastName.required" class="help is-danger">
                {{ $t("auth.error_last_name") }}
              </p>
            </template>
          </div>

          <div class="field">
            <label class="label has-text-primary">{{ $t("auth.email") }}</label>
            <div class="control">
              <input
                class="input is-simple"
                type="text"
                :disabled="true"
                :placeholder="$t('auth.email')"
                v-model="email"
                :class="{ 'is-danger': $v.email.$error && validateForm }"
                @input="updateEmail"
              />
            </div>
            <template v-if="$v.email.$error && validateForm">
              <p v-if="!$v.email.required" class="help is-danger">
                {{ $t("auth.error_email") }}
              </p>
              <p v-if="!$v.email.email || emailNotFoundError" class="help is-danger">
                {{ $t("auth.error_email") }}
              </p>
            </template>
          </div>

          <custom-register-form
            ref="customForm"
            class="mt-4"
            :include-register-form="false"
            :current-locale="currentLocale"
            update-type="input"
            :form-data="formData"
            @input="updateFormMetaData($event)"
          >
          </custom-register-form>

          <button class="button is-primary has-fullwidth mt-2" @click="onEmailSubmit">
            <div
              class="is-flex is-flex-direction-row is-justify-content-center is-align-items-center"
            >
              <span class="mr-1">{{ $t("common.send") }}</span>
              <span>
                <icon-right class="has-stroke-primary-color-text"></icon-right>
              </span>
            </div>
          </button>
        </template>

        <b-loading
          :is-full-page="false"
          :active="isRequesting"
          :can-cancel="false"
        ></b-loading>
      </div>
      <div class="google-header-completed" v-if="!tokenQuery">
        <google-header class="svg-icon" ></google-header>
      </div>
      <div class="google-header" v-else-if="(isGoogleSummit2024 && !isSuccess && !currentUserTicket)">
        <google-header class="svg-icon"></google-header>
      </div>
      <div class="google-header-completed" v-else>
        <google-header class="svg-icon" ></google-header>
      </div>
    </div>
  </article>
</template>

<script>
import LpConfigMixin from "@/shared/mixins/LpConfigMixin";
import { mapActions, mapGetters, mapState } from "vuex";
import LpConfigConstants from "@/shared/constants/lp-config-constants";
import CustomRegisterForm from "@/web/components/forms/CustomRegisterForm";
import FormBuilderConstants from "@/web/views/dev/formbuilder/form-builder-constants";
import RequestMixin from "@/shared/mixins/RequestMixin";
import { debounce } from "@/shared/utils";
import IconRight from "@/assets/icon_carousel_arrow_right.svg";
import { email, required } from "vuelidate/lib/validators";
import Constants from "@/web/constants";
import EditPasswordForm from "@/web/components/forms/EditPasswordForm";
import axios from "@/web/services/axios";

import GoogleLogo from "@/assets/custom/google/google_logo.svg";
import GoogleHeader from "@/assets/custom/google/google_summit_2024_header.svg";

export default {
  name: "SpreadsheetFormComponent",
  mixins: [LpConfigMixin, RequestMixin],
  components: {
    CustomRegisterForm,
    GoogleLogo,
    GoogleHeader,
    IconRight,
    EditPasswordForm,
  },

  props: {
    moduleData: {
      type: Object,
      required: true,
    },
  },

  mounted() {
    this.fetch({ eventId: this.eventId });
    if (this.currentUser) {
      this.email = this.currentUser.email;
      this.firstName = this.currentUser.first_name;
      this.lastName = this.currentUser.last_name;
    }
  },

  data() {
    return {
      isSuccess: false,
      currentStep: 0,
      email: this.$route.query.email,
      firstName: this.$route.query.first_name,
      lastName:  this.$route.query.last_name,
      checkboxesFormData: null,
      emailSubmitted: false,
      emailNotFoundError: false,
      validateForm: false,
      passwordSet: false,
      isAlreadyFilled: false,
    };
  },

  validations: {
    email: {
      required,
      email: (value) => email(value.toLowerCase().replace(/\s/g, "")),
    },
    firstName: {
      required,
    },
    lastName: {
      required,
    },
  },

  requests: {
    async submit(body) {
      const response = await axios.post(`events/${this.eventId}/payment/free/init`, body);
      this.onSuccess();
    },

    async updatePassword(credentials) {
      await this.updateUserPasswordWithToken({ ...credentials, token: this.tokenQuery, gdpr: true, privacy: true });
      this.onSetPasswordSuccess();
    },
  },

  methods: {
    ...mapActions("eventTickets", ["fetch"]),
    ...mapActions("auth", ["updateUserPasswordWithToken"]),

    updateFormMetaData(formData) {
      this.checkboxesFormData = formData;
    },

    onSubmit: debounce(function (formData) {
      let requestBody = {
        ticket_id: this.ticket.id,
        tickets_for: [],
        token: this.tokenQuery,
        personal_form: {
          first_name: this.firstName,
          last_name: this.lastName,
          email: this.email.toLowerCase().replace(/\s/g, ""),
          addons: [],
        },
        specific_fields: this.checkboxesFormData.metaData,
      };
      this.submit(requestBody);
    }, 750),

    onSuccess() {
      this.isSuccess = true;
      document.querySelector(".event-wrapper").scrollIntoView({ block: "start" });
    },

    onEmailSubmit() {
      const customForm = this.$refs.customForm;
      customForm.touch();
      let isValid = customForm.isValid();

      this.$v.$touch();
      this.validateForm = true;
      isValid = isValid && !this.$v.$anyError;

      if (isValid) {
        this.onSubmit();
      }
    },

    resetForm() {
      this.currentStep = 0;
      this.emailSubmitted = false;
      this.isSuccess = false;
      this.error = null;
      this.passwordSet = false;
      this.isAlreadyFilled = false;
      this.validateForm = false;
      this.emailNotFoundError = false;
    },

    updateEmail(value) {
      this.emailNotFoundError = false;
    },

    openChangePassword() {
      this.currentStep = 1;
    },

    openMyProfile() {
      this.$router.push({ name: Constants.ROUTE_MY_PROFILE });
    },

    onSetPasswordSuccess() {
      this.passwordSet = true;
      this.currentStep = 0;
    },
  },
  computed: {
    ...mapState(["eventId"]),
    ...mapGetters("locales", ["currentLocale"]),
    ...mapGetters("eventTickets", ["getById"]),
    ...mapGetters("currentUser", ["currentUser"]),
    ...mapGetters("auth", ["isAuthorized"]),
    ...mapGetters("userTickets", { currentUserTicket: "ticket" }),

    commonModuleFields: () => LpConfigConstants.COMMON_MODULE_FIELDS,

    title() {
      return this.getCustomTranslation(
        this.moduleData,
        this.commonModuleFields.TITLE,
        null
      );
    },

    isGoogleSummit2024() {
      return this.eventId === 1623;
    },

    ticket() {
      if (this.eventId === 1387) {
        return this.getById(497);
      } else if (this.eventId === 298) {
        return this.getById(195);
      } else if (this.eventId === 1623) {
        return this.getById(870);
      } else {
        return null;
      }
    },

    formData() {
      if (this.ticket) {
        return JSON.parse(this.ticket.custom_form.json_config.replace(/\\\\n/g, "\\n"));
      } else {
        return null;
      }
      // return this.ticket && JSON.parse(this.ticket.form_data);
      // if (this.eventId === 298 || this.eventId === 1387) {
      //   return JSON.parse(
      //     '{"fields":[{"id":"checkbox_1","order":1,"type":"checkbox","required":true,"disabled":false,"rules":[]},{"id":"checkbox_2","order":2,"type":"checkbox","required":false,"disabled":false,"rules":[],"collapsed":true}],"translations":{"en":{"email_label":"Email","checkbox_4_label":"Agreement 4(optional)","checkbox_3_label":"Agreement 3(optional)","checkbox_2_label":"Wyrażam zgodę na przekazanie przez Administratora danych osobowych: imienia, nazwiska, adresu e -mail partnerom Konferencji „Google Cloud Summit Poland” (zwanych dalej „Partnerami Konferencji”) w celu wykorzystania ich na potrzeby marketingowe.\\n\\n Cele marketingowe umożliwiać będą kontaktowanie się z Tobą w przyszłości w związku z wydarzeniami, projektami, produktami i usługami świadczonymi przez Partnerów Konferencji. Każdy Partner będzie odpowiedzialny za zapewnienie, że Twoje dane osobowe będą przetwarzane zgodnie z RODO, głównie za określenie celu przetwarzania i zgodności z prawem przetwarzania Twoich danych oraz za zapewnienie Ci wszelkich niezbędnych informacji wymaganych przez RODO, zajmie się także wszelkimi żądaniami i realizacją praw wynikających z RODO w związku z przetwarzaniem Twoich danych przez Partnera Konferencji oraz zbieraniem wszelkich niezbędnych zgód wymaganych przez RODO lub inne przepisy prawa. Więcej na temat przetwarzania danych oraz prawie cofnięcia zgody w {0}.\\n\\n","checkbox_1_label":"*Oświadczam, iż zapoznałem/am się z {0} i zawartym w nim obowiązkiem informacyjnym dotyczącym przetwarzania danych osobowych","checkbox_1_substrings":["Regulaminem Konferencji"],"checkbox_1_links":["https://drive.google.com/file/d/1lSXLFQkIT6YGLCPVgmFq9urc_ue5FQSd/view"],"checkbox_2_substrings":["Regulaminie Konferencji"],"checkbox_2_links":["https://drive.google.com/file/d/1lSXLFQkIT6YGLCPVgmFq9urc_ue5FQSd/view"],"checkbox_2_button":"Wyświetl Partnerów Konferencji","checkbox_2_collapsed_text_labels":"- Deloitte Poland sp. z o.o. z siedzibą w Warszawie pod adresem: Al. Jana Pawła II 22, 00-133 Warszawa, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego, prowadzonego przez Sąd Rejonowy dla m.st. Warszawy w Warszawie, XII Wydział Gospodarczy Krajowego Rejestru Sądowego pod numerem KRS KRS0000004728NIP527-020-73-28, posiadającą kapitał zakładowy w wysokości 20 226 507,00 PLN\\n\\n- Accenture Sp. z o.o. z siedzibą przy ul. Siennej 39, 00-121 Warszawa, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000019271, NIP: 5260015900, REGON: 010360920 - PwC Advisory spółka z ograniczoną odpowiedzialnością sp.k z siedzibą w Warszawie przy ul. Polnej 11, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000741448, NIP: 1132399979\\n\\n- Operatora Chmury Krajowej sp. z o.o. z siedzibą w Warszawie przy ul.Grzybowskiej 62, 00-844 Warszawa, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000770202, której dokumentacja rejestrowa jest przechowywana przez Sąd Rejonowy dla m.st. Warszawy w Warszawie, Wydział XIII Gospodarczy Krajowego Rejestru Sądowego, NIP: 5252775789, REGON: 382039032\\n\\n- Devoteam Poland sp. z o.o. z siedzibą w Warszawie przy ul. Waryńskiego 3a., 00-645 Warszawa, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000536413, NIP: 5252600363, REGON:360090346\\n\\n- GFT Poland sp. z o.o.z siedzibą w Łodzi przy ul. Kilińskiego 66, 90-118 Łódź, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000284121, NIP: 7251947829, REGON: 100373005\\n\\n- Fly On The Cloud Sp. z o.o. z siedzibą we Wrocławiu przy ul. Rzeźniczej 32/33, 50-130 Wrocław, wpisana do rejestru przedsiębiorców prowadzonego przez Sąd Rejonowy dla Wrocławia Fabrycznej, Wydział VI Krajowego Rejestru Sądowego, pod numerem KRS 0000500884, NIP 8971797086\\n\\n - Sii Sp z o.o.. z siedzibą w Warszawie przy Al. Niepodległości 69, 02-626 Warszawa, wpisaną do Rejestru Przedsiębiorców Krajowego Rejestru Sądowego prowadzonego przez Sąd Rejonowy dla m.st. Warszawy w Warszawie, XII Wydział Gospodarczy Krajowego Rejestru Sądowego pod numerem KRS 0000249203, NIP: 5252352907, REGON: 140381516\\n\\n- Palo Alto - CRS a.s z siedzibą w Pradze/ Czechy przy ul.Klapkova 546 182 00 Prague 8, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego VAT: CZ28387741 ID: 28387741\\n\\n- MongoDB Poland spółka z ograniczoną odpowiedzialnością z siedzibą w Warszawie przy ul.Józefa Piusa Dziekońskiego 1, 00-728, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 000085524 NIP: PL5272933926\\n\\n- Atende SA z siedzibą w Warszawie przy placu Konesera 10A, 03.-736, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000320991, NIP: 954-23-57-358., REGON: 276930771\\n\\n- SOFTSERVE POLAND Sp. z o.o. z siedzibą przy ul. Jaworskiej 11-13, 53-612 we Wrocławiu, wpisaną do Rejestru Przedsiębiorców Krajowego Rejestru Sądowego przez Sąd Rejonowy dla Wrocławia-Fabrychnej we Wrocławu, VI Wydział Gospodarczy KRS Wydział Gospodarczy Krajowego Rejestru Sądowego pod numerem KRS: 0000395752, NIP: 5222987736, REGON: 145164189\\n\\n- Alterdata.io sp. z o.o., z siedzibą w Warszawie (02-672), ul. Domaniewska 47/10, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego prowadzonego przez Sąd Rejonowy w Warszawie, XIII Wydział Gospodarczy, pod numerem KRS 0000672717,  NIP PL 521-377-59-57, REGON 36700511900000\\n\\n- OpenText Sp. z o.o. z siedzibą w Poznaniu przy ul. Baraniaka 6, 61-626 Poznań, wpisaną do rejestru przedsiębiorców Krajowego Rejestru Sądowego pod numerem KRS 0000077053, NIP: 7781387031, REGON: 634111467"}}}'
      //   );
      // } else if (this.eventId === 299 || this.eventId === 1399) {
      //   return JSON.parse(
      //     '{"fields":[{"id":"checkbox_1","order":0,"type":"checkbox","required":true,"disabled":false,"rules":[]},{"id":"checkbox_2","order":1,"type":"checkbox","required":false,"disabled":false,"rules":[],"collapsed":true}],"translations":{"en":{"checkbox_1_substrings":["Regulamentul Conferinței"],"checkbox_1_links":["https://drive.google.com/file/d/1foO5kUUPrSDdOnZNYJ-EptxsauyNbpUd/view"],"checkbox_1_label":"Declar că am citit {0} și obligația în materie de informare privind prelucrarea datelor cu caracter personal conținută în acesta.","checkbox_2_label":"Sunt de acord ca Operatorul să transmită datele mele cu caracter personal, cum ar fi: numele, prenumele, adresa de e-mail partenerilor Conferinței \\"Google Cloud Day România\\" (denumiți în continuare \\"Partenerii Conferinței\\") pentru a fi utilizate în scopuri de marketing.\\n\\nScopurile de marketing vă vor permite să fiți contactat(ă) în viitor în legătură cu evenimente, proiecte, produse și servicii furnizate de Partenerii Conferinței. Fiecare Partener va fi responsabil pentru a se asigura că datele dvs. cu caracter personal sunt prelucrate în conformitate cu GDPR, în principal pentru a determina scopul prelucrării și legalitatea prelucrării datelor dvs. și pentru a vă furniza toate informațiile necesare cerute de GDPR, acesta se va ocupa, de asemenea, de orice solicitare și de exercitarea drepturilor dvs. în temeiul GDPR în legătură cu prelucrarea datelor dvs. de către Partenerul Conferinței și de colectarea oricăror consimțăminte necesare cerute de GDPR sau de alte legi. Pentru mai multe informații privind prelucrarea datelor și dreptul de retragere a consimțământului, vă rugăm să consultați {0}\\n\\n","checkbox_2_substrings":["Regulamentul Conferinței"],"checkbox_2_links":["https://drive.google.com/file/d/1foO5kUUPrSDdOnZNYJ-EptxsauyNbpUd/view"],"checkbox_2_button":"Vezi Partenerii Conferinței","checkbox_2_collapsed_text_labels":"- Europe Cloud Ltd. with its registered office in Varbitsa 5, fl. 1, ap. 2 1504 Sofia, Bulgaria,Tax Identification Number: BG205475953\\n\\n- Cloud Office with its registered office in Bulgaria, Sofia, Georgi Benkovski 37 str street, entered in National Court Register under the KRS noBG205743223, NIP: 205743223, REGON: Bulgaria\\n\\n- Devoteam Poland sp. z o.o. with its registered office in ul. Waryńskiego 3a., 00-645 Warszawa, entered in National Court Register under the KRS no 0000536413, NIP:5252600363 REGON: 360090346\\n\\n- Sense8 Digital Technology SRL with its registered office in Romania, 32 Londra street,Bucharest, entered in National Court Register under the no. J40/19046/2006, VAT ID. RO19228205\\n\\n- Zitec Com SRL with its registered office in 165 Splaiul Unirii TNO2 Building, 6th floor 3rd district, 030133 Bucharest,Romania street, entered in National Court Register under the KRS no.J40/7701/2003, NIP: RO 15496736\\n\\n- VMWare International Unlimited Company, address: Parnell House, Barrack Square, Ballincollig, Co. Cork, Ireland.,VAT: IE6422208G "}}}'
      //   );
      // } else {
      //   return null;
      // }
    },

    isFirstStep() {
      return this.currentStep === 0;
    },

    isSecondStep() {
      return this.currentStep === 1;
    },

    firstNameQuery() {
      return this.$route.query.first_name;
    },

    lastNameQuery() {
      return this.$route.query.last_name;
    },

    emailQuery() {
      return this.$route.query.email;
    },

    tokenQuery() {
      return this.$route.query.token;
    },
  },

  watch: {
    formData: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          this.$mergeCustomTranslations(
            newValue[FormBuilderConstants.TRANSLATIONS],
            FormBuilderConstants.TRANSLATIONS_NAMESPACE
          );
        }
      },
    },

    currentUser: {
      handler: function (newValue) {
        if (newValue) {
          this.resetForm();
          this.$refs.customForm?.reset();
          this.email = newValue.email;
          this.firstName = newValue.first_name;
          this.lastName = newValue.last_name;
        } else {
          this.email = this.emailQuery;
          this.firstName = this.firstNameQuery;
          this.lastName = this.lastNameQuery;
          this.resetForm();
          this.$refs.customForm?.reset();
        }
      },
      immediate: false,
      deep: true,
    },

    error(newVal) {
      if (newVal) {
        if (
          (newVal.response.data.errors && newVal.response.data.errors.length) ||
          newVal.response.data.length
        ) {
          let error =
            (newVal.response.data.errors && newVal.response.data.errors[0].code) ||
            (newVal.response.data && newVal.response.data[0].error);
          if (
            error === "payment.ticket.user_already_have_ticket" ||
            error === "spreadsheet.form_already_filled"
          ) {
            this.$root.openSuccessModal(this.$t('custom.spreadsheet_form_again_title'), this.$t('custom.spreadsheet_form_again_desc', { 0: 'support@meetingapplication.com' }));
            this.isAlreadyFilled = true;
            this.onSuccess();
          } else if (error === "payment.email_not_found") {
            this.$root.openErrorModal(
              this.$t("custom.google_spreadsheet_email_not_found_error")
            );
            this.currentStep = 0;
            this.emailNotFoundError = true;
          } else if (error === "event_not_configured") {
            this.$root.openErrorModal(
              this.$t("custom.google_spreadsheet_event_not_configured_error")
            );
            this.resetForm();
          } else {
            this.$root.openErrorModal(error);
            this.resetForm();
          }
        }
        this.error = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.google-header {
  position: absolute;
  left: -600px;
  bottom: 100px;
  width: 1068px;
}

.google-header-completed {
  position: absolute;
  left: -345px;
  bottom: -270px;
  max-width: 600px;
  width: 600px;
}

.google-form-container {
  margin-top: 120px;
  max-width: 620px;
  margin-bottom: 350px;
  margin-bottom: 90px;
  width: 100%;
  position: relative;

  &__content {
    z-index: 10;
    border-radius: 26px;
    position: relative;
  }
}

.google-footer-1 {
}

.google-footer-2 {
}

.module {
  margin-bottom: 0px !important;
}

.google-footer-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

@media only screen and (max-width: 1355px) {
  .google-header {
    display: none;
  }
  .google-footer-2 {
    display: none;
  }
}
</style>
